import { Component, OnInit, Input, Renderer2, OnDestroy, Inject, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { DataService } from 'src/app/shared/services/data.service';
import { environment } from 'src/environments/environment'; 
import { DOCUMENT } from '@angular/common';
import { RouterService } from 'src/app/shared/services/router.service';

@Component({
  selector: 'app-articles-and-news-page',
  templateUrl: './articles-and-news-page.component.html',
  styleUrls: ['./articles-and-news-page.component.scss', './articles-and-news-page.component.en.scss'],
})
export class ArticlesAndNewsPageComponent implements OnInit, OnDestroy {
  @Input() pageUrl: string | null = null;

  pageData: any;
  currentPage: number = 1;

  paginatorContainerCssClass = 'pagination';
  rangeSize: number = 3;

  isMobile: boolean = false;
  isLoading: boolean = false;
  mobileNextPageLoading: boolean = false;
  showFiltersMobileModal: boolean = false;

  searchTerm: string = '';
  searchTermObj = new Subject<string>();
  searchRequest: any = {
    startDate: null,
    endDate: null,
    contentTypes: [],
    subjectsList: []
  };

  private ngUnsubscribeUrlListener: Subject<void> = new Subject<void>();

  constructor(
    private dataService: DataService,
    private routerService: RouterService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.isMobile = window.innerWidth <= 640;

    this.searchTermObj.pipe(debounceTime(1000)).subscribe((term) => {
      this.searchTerm = term;
      this.searchAriclesAndNews();
    });
  }

  ngOnInit(): void {
    this.changeAddithinalStyles();
    this.handelUrlParameters();
    this.getIndexData();
  }

  ngOnDestroy(): void {
    this.changeAddithinalStyles(true);
    this.ngUnsubscribeUrlListener.next();
    this.ngUnsubscribeUrlListener.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    let width = (event.target as Window).innerWidth;
    this.isMobile = width <= 640;
  }

  private changeAddithinalStyles(destroy: boolean = false) {
    let element = this.document.querySelector('.page-content');
    if (element) {
      if (destroy) {
        this.renderer.removeStyle(element, 'padding');
      } else {
        this.renderer.setStyle(element, 'padding', 'inherit');
      }
    }

    element = this.document.querySelector(
      '.page-content app-standard-content-page app-card-header'
    );
    if (element) {
      if (destroy) {
        this.renderer.removeStyle(element, 'display');
      } else {
        this.renderer.setStyle(element, 'display', 'none');
      }
    }

    element = this.document.querySelector('.portal-body .page-content-wrap');
    if (element) {
      if (destroy) {
        this.renderer.removeStyle(element, 'background-color');
      } else {
        this.renderer.setStyle(element, 'background-color', 'white');
      }
    }
  }

  getIndexData() {
    this.isLoading = true;
    let url = environment.apiUrl + '/Portal/Articles-Api/Index';
    let request: any = {
      pageUrl: this.pageUrl ?? this.routerService.getCurrentComponentRoute(),
      cultureCode: this.dataService.getCultureCode(),
      currentPage: this.currentPage,
      searchTerm: this.searchTerm,
      startDate: this.searchRequest.startDate,
      endDate: this.searchRequest.endDate,
      contentTypes: this.searchRequest.contentTypes,
      subjectsList: this.searchRequest.subjectsList
    };

    this.dataService.getDataByToken(url, request).subscribe({
      next: (responseData) => {

        responseData.articlesContentTypes = responseData.articlesContentTypes.map((c: any) => {
          return {
            id: c.id,
            name: c.name,
            selected: this.searchRequest.contentTypes.includes(c.id),
            disabled: false,
            iconUrl: c.iconUrl
          }
        });

        responseData.articlesSubjectsList = responseData.articlesSubjectsList.map((c: any) => {
          return {
            id: c.key,
            name: c.value,
            selected: this.searchRequest.subjectsList.includes(c.key),
            disabled: false
          }
        });

        this.pageData = responseData;
        this.isLoading = false;
      },
      error: (e) => {
        console.error(e);
      },
    });
  } 

  searchAriclesAndNews(pageMove: boolean = false, mobileNextPage: boolean = false) {
    if (!pageMove) {
      this.currentPage = 1;
    }

    let url = environment.apiUrl + '/Portal/Articles-Api/GetArticlesAndNews';
    let request: any = {
      cultureCode: this.dataService.getCultureCode(),
      pageSize: this.pageData.pageSize,
      currentPage: this.currentPage,
      searchTerm: this.searchTerm,
      startDate: this.searchRequest.startDate,
      endDate: this.searchRequest.endDate,
      contentTypes: this.searchRequest.contentTypes,
      subjectsList: this.searchRequest.subjectsList
    };

    this.isLoading = true;
    this.updateUrlParameters();
    this.dataService.getDataByToken(url, request).subscribe({
      next: (responseData) => {
        if(mobileNextPage) {
          this.pageData.articlesList.articlesList = [...this.pageData.articlesList.articlesList, ...responseData.articlesList];
          this.mobileNextPageLoading = false;
        } else {
          this.pageData.articlesList = responseData;
        }
        this.isLoading = false;
      },
      error: (e) => {
        console.error(e);
        this.isLoading = false;
        this.mobileNextPageLoading = false;
      },
    });
  }

  onInputChange(event: any) {
    this.searchTermObj.next(event.target.value);
  }

  changeStartDate(event: Date) {
    this.searchRequest.startDate = this.toISOStringLocal(event);
    this.searchAriclesAndNews();
  }

  changeEndDate(event: Date) {
    this.searchRequest.endDate = this.toISOStringLocal(event);
    this.searchAriclesAndNews();
  }

  private toISOStringLocal(date: Date): string {
    function pad(number: number) {
        if (number < 10) {
            return '0' + number;
        }
        return number;
    }

    return date.getFullYear() +
        '-' + pad(date.getMonth() + 1) +
        '-' + pad(date.getDate()) +
        'T' + pad(date.getHours()) +
        ':' + pad(date.getMinutes()) +
        ':' + pad(date.getSeconds()) +
        '.' + (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5) + 'Z';
  }

  goToPage(pageNumber: number) {
    if (event) event.preventDefault();
    if (
      this.pageData.articlesList.articlesList &&
      this.pageData.articlesList.articlesList.length &&
      pageNumber >= 1 &&
      pageNumber <=
        Math.ceil(
          this.pageData.articlesList.totalResults /
            this.pageData.pageSize
        )
    ) {
      this.currentPage = pageNumber;
      this.searchAriclesAndNews(true);
    }
  }

  getContentShowing(): boolean {
    return this.pageData && 
    this.pageData.articlesList.articlesList &&
    this.pageData.articlesList.articlesList.length &&
    !this.isLoading;
  }

  clearSearch($event: any) {
    $event.preventDefault();

    this.searchTerm = '';
    this.searchRequest = {
      startDate: null,
      endDate: null,
      contentTypes: [],
      subjectsList: []
    };

    this.pageData.articlesContentTypes.forEach((c: any) => { c.selected = false; });
    this.pageData.articlesSubjectsList.forEach((c: any) => { c.selected = false; });
    
    this.searchAriclesAndNews();
  }

  openFiltersMobilePopup() {
    this.showFiltersMobileModal = true;
    this.changeSidebarMenu(false);
  }

  closeFiltersMobilePopup() {
    this.showFiltersMobileModal = false;
    this.changeSidebarMenu(true);
  }

  changeSidebarMenu(show: boolean) {
    let element = this.document.querySelector('app-sidebar');
    if(!element) return;
    if(show) {
      this.renderer.removeStyle(element, 'display');
    } else {
      this.renderer.setStyle(element, 'display', 'none');
    }
  }

  contentTypesChanged(event: any) {
    this.searchRequest.contentTypes = event;
    this.pageData.articlesContentTypes.forEach((c: any) => {
      c.selected = event.includes(c.id);
    });
    this.searchAriclesAndNews();
  }

  contentTypesChangedMobile(filter: any) {
    let index = this.pageData.articlesContentTypes.findIndex((f: any) => f.id == filter.id);
    if(index != -1) {
      this.pageData.articlesContentTypes[index].selected = filter.checked;
      let selectedIndex = this.searchRequest.contentTypes.indexOf(filter.id);
      if(selectedIndex == -1 && filter.checked) {
        this.searchRequest.contentTypes = [...this.searchRequest.contentTypes, filter.id];
      } else {
        if(!filter.checked) {
          this.searchRequest.contentTypes = this.searchRequest.contentTypes.filter((id: any) => id !== filter.id);
        }
      }
    }

    this.searchAriclesAndNews();
  }

  articlesSubjectsChange(filter: any) {
    let index = this.pageData.articlesSubjectsList.findIndex((f: any) => f.id == filter.id);
    if(index != -1) {
      this.pageData.articlesSubjectsList[index].selected = !this.pageData.articlesSubjectsList[index].selected;
    }

    this.searchAriclesAndNews();
  }

  articlesSubjectsChangeMobile(filter: any) {
    let index = this.pageData.articlesSubjectsList.findIndex((f: any) => f.id == filter.id);
    if(index != -1) {
      this.pageData.articlesSubjectsList[index].selected = filter.checked;
      let selectedIndex = this.searchRequest.subjectsList.indexOf(filter.id);
      if(selectedIndex == -1 && filter.checked) {
        this.searchRequest.subjectsList = [...this.searchRequest.subjectsList, filter.id];
      } else {
        if(!filter.checked) {
          this.searchRequest.subjectsList = this.searchRequest.subjectsList.filter((id: any) => id !== filter.id);
        }
      }
    }
    this.searchAriclesAndNews();
  }

  nextPageMobile() {
    this.currentPage++;
    this.mobileNextPageLoading = true;
    this.searchAriclesAndNews(true, true);
  }

  getMoreMobileCount(): number {
    if(!this.pageData) return 0;

    return this.pageData.articlesList.totalResults - this.pageData.articlesList.articlesList.length;
  }

  getDisplayingCountText(textFormat: string): string {
    if(textFormat.indexOf('{0}') == -1) 
      return textFormat + ' ' + this.pageData.articlesList.totalResults;
    else
      return textFormat.replace('{0}', this.pageData.articlesList.totalResults);
  }

  private handelUrlParameters() {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribeUrlListener)).subscribe({
      next: (params) => {
        this.searchTerm = params['term'] ?? '';
        this.searchRequest.startDate = params['startDate'] ? this.parseDate(params['startDate']) : null;
        this.searchRequest.endDate = params['endDate'] ? this.parseDate(params['endDate']) : null;
        this.searchRequest.contentTypes = params['contentTypes'] ? params['contentTypes'].split(',').map(Number) : [];
        this.searchRequest.subjectsList = params['subjectsList'] ? params['subjectsList'].split(',').map(Number) : [];

        let page = params['page'];
        let validPage = page && parseInt(page) >= 1;
        this.currentPage = validPage ? parseInt(page) : 1;
      },
      error: (e) => console.error('Error while fetching query params', e),
    });
  }

  private updateUrlParameters() {
    let queryParams: any = {};

    if (this.currentPage > 1)
      queryParams.page = this.currentPage;

    if (this.searchTerm != undefined && this.searchTerm !== '')
      queryParams.term = this.searchTerm;

    if (this.searchRequest.startDate) {
      queryParams.startDate = this.formatDate(this.searchRequest.startDate);
    }

    if (this.searchRequest.endDate) {
      queryParams.endDate = this.formatDate(this.searchRequest.endDate);
    }

    if (this.searchRequest.contentTypes.length > 0) {
      queryParams.contentTypes = this.searchRequest.contentTypes.join(',');
    }

    if (this.searchRequest.subjectsList.length > 0) {
      queryParams.subjectsList = this.searchRequest.subjectsList.join(',');
    }

    queryParams = Object.keys(queryParams).length > 0 ? queryParams : null;

    var url = this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: queryParams,
      })
      .toString();

    window.history.pushState({ path: url }, '', url);
  }

  private formatDate(dateString: string): string {
    let date = new Date(dateString);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  }

  private parseDate(dateString: string): Date {
    let parts = dateString.split('.');
    let year = parseInt(parts[2], 10);
    let month = parseInt(parts[1], 10) - 1;
    let day = parseInt(parts[0], 10);
    return new Date(Date.UTC(year, month, day));
  }
}